/**
 * Managed User Scorecard Context Service
 *
 * Application service for hydrating sidebar scorecard context in manager flows.
 *
 * Why this service exists
 * - Isolates API orchestration from feature-hook UI concerns.
 * - Centralizes score/simulation/certification relationship resolution.
 * - Provides a stable, testable contract for managed-user scorecard loading.
 */
import { getScoreCardBySession, getUserAvailables } from '@/lib';
import {
  mapApiAvailablesToFrontend,
  mapApiScorecardsToFrontend,
  mapApiSimulationsToFrontend,
} from '@/utils';

/**
 * Hydrated sidebar context for a single managed-user session scorecard.
 *
 * Notes
 * - `score` is the primary entity; simulation/certification are resolved from
 *   the score's linked ids.
 * - Any unresolved relationship returns `null` instead of throwing, preserving
 *   the existing defensive UI contract.
 */
export interface ManagedUserScorecardContext {
  /** Certification linked to resolved score.certificationId, if found. */
  certification: ReturnType<typeof mapApiAvailablesToFrontend>[number] | null;
  /** Primary mapped scorecard entity for the requested session. */
  score: ReturnType<typeof mapApiScorecardsToFrontend>[number] | null;
  /** Simulation linked to resolved score.simulationId, if found. */
  simulation: ReturnType<typeof mapApiSimulationsToFrontend>[number] | null;
}

/**
 * Input contract for managed-user scorecard context hydration.
 */
export interface LoadManagedUserScorecardContextInput {
  /** Managed user identifier required by the availables endpoint. */
  managedUserId: string;
  /** Session id used to fetch the target scorecard. */
  sessionId: number;
}

/**
 * Load and resolve sidebar context for a managed-user scorecard session.
 *
 * Execution flow
 * 1. Fetch raw scorecard + availables in parallel.
 * 2. Map scorecard payload into frontend score entities.
 * 3. Resolve linked simulation and certification by id from mapped collections.
 * 4. Return nullable-safe context consumed by sidebar UI/store.
 *
 * Relationship rules
 * - If no mapped score is available, simulation and certification are both `null`.
 * - Matching compares stringified ids to tolerate number/string source variance.
 *
 * @param input Managed-user + session identifiers.
 * @returns Sidebar-ready managed-user scorecard context.
 */
export const loadManagedUserScorecardContext = async ({
  managedUserId,
  sessionId,
}: LoadManagedUserScorecardContextInput): Promise<ManagedUserScorecardContext> => {
  // ================================
  // Step 1: Fetch Raw API Payloads (parallel)
  // ================================
  // Both resources are required to map and resolve linked entities.
  const [apiScorecard, apiAvailables] = await Promise.all([
    getScoreCardBySession(sessionId),
    getUserAvailables(managedUserId),
  ]);

  // ================================
  // Step 2: Resolve Primary Score Entity
  // ================================
  // This flow expects one scorecard for the requested session. We still use
  // `[0] || null` defensively in case mapper output changes or is empty.
  const score =
    mapApiScorecardsToFrontend({
      apiAvailables,
      apiScorecards: [apiScorecard],
    })[0] || null;

  // ================================
  // Step 3: Resolve Simulation Relationship
  // ================================
  // Only attempt relationship lookup when primary score exists.
  const simulation = score
    ? mapApiSimulationsToFrontend({ apiAvailables, apiScorecards: [apiScorecard] }).find(
        (sim) => sim.id.toString() === score.simulationId,
      ) || null
    : null;

  // ================================
  // Step 4: Resolve Certification Relationship
  // ================================
  // Similar to simulation resolution, keyed by score.certificationId.
  const certification = score
    ? mapApiAvailablesToFrontend({ apiAvailables, apiScorecards: [apiScorecard] }).find(
        (cert) => cert.id.toString() === score.certificationId,
      ) || null
    : null;

  // ================================
  // Step 5: Return Stable Nullable Contract
  // ================================
  return {
    certification,
    score,
    simulation,
  };
};
